/**
 * Shared impact definitions used across the platform.
 *
 * Single source of truth for how we describe DALYs, PDFs, USD,
 * impact categories, fuel types, and project lifecycle terminology.
 */

// ---------------------------------------------------------------------------
// Endpoint impact categories (the three "hero" metrics)
// ---------------------------------------------------------------------------

export interface EndpointDefinition {
  title: string;
  shortTitle: string;
  unit: string;
  burdenUnit: string;
  description: string;
}

export const ENDPOINT_DEFINITIONS: Record<string, EndpointDefinition> = {
  health: {
    title: 'Human Health',
    shortTitle: 'Health',
    unit: 'DALYs Avoided',
    burdenUnit: 'DALYs net impact',
    description:
      'Disability-Adjusted Life Years (DALYs) measure years of healthy life saved by ' +
      'displacing fossil fuel pollution. Covers avoided harm from air pollution ' +
      '(particulate matter, ozone), toxic emissions (heavy metals, carcinogens), ' +
      'and climate-related health impacts. One DALY equals one year of full health ' +
      'lost to illness or premature death.',
  },
  ecosystem: {
    title: 'Ecosystem Quality',
    shortTitle: 'Biodiversity',
    unit: 'Species-Years Protected',
    burdenUnit: 'species·yr net impact',
    description:
      'Potentially Disappeared Fraction of species (PDF), integrated over area and ' +
      'time, measures biodiversity protected by displacing fossil fuels. Covers ' +
      'reduced acidification, ecotoxicity, eutrophication, and land use from ' +
      'avoided fossil fuel extraction. Higher values mean more species habitat preserved.',
  },
  economic: {
    title: 'Economic Impact',
    shortTitle: 'Economic',
    unit: 'USD',
    burdenUnit: 'USD',
    description:
      'Total monetized value of all avoided environmental and health damages, ' +
      'calculated using ReCiPe 2016 endpoint damage cost factors. Includes health care ' +
      'costs, lost productivity, ecosystem service losses, and resource depletion.',
  },
};

// ---------------------------------------------------------------------------
// Category display name mapping (model output → ReCiPe standard)
//
// The model returns names like "Carbon (Climate Change)". We display
// the ReCiPe 2016 standard names instead.
// ---------------------------------------------------------------------------

const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  'Carbon':                                'Climate Change',
  'Carbon (Climate Change)':               'Climate Change',
  // Legacy engine labels for the ReCiPe land-use category → canonical 'Land Use'.
  // Kept so results from runs on older model images still render correctly.
  'Land Saved by Reduced Fuel Extraction': 'Land Use',
  'Land Use (Fuel Extraction)':            'Land Use',
  'Water Depletion':                       'Water Use',
};

// Categories that should NEVER render in user-facing tables/charts,
// regardless of what the backend returns. Currently empty — kept as a
// hook for future rename/scrub tickets.
const HIDDEN_CATEGORIES = new Set<string>([]);

export function isHiddenCategory(category: string | undefined | null): boolean {
  if (!category) return false;
  return HIDDEN_CATEGORIES.has(category);
}

/**
 * Returns the ReCiPe-standard display name for an impact category.
 * Falls back to the original name if no mapping exists.
 */
export function getCategoryDisplayName(category: string): string {
  return CATEGORY_DISPLAY_NAMES[category] ?? category;
}

// ---------------------------------------------------------------------------
// Midpoint impact categories (the detailed breakdown rows)
//
// Keys match what the model returns in summary_json.Impacts.*.Impact_Category
// so tooltip lookup works. Definitions are ReCiPe 2016 descriptions.
// ---------------------------------------------------------------------------

export const MIDPOINT_DEFINITIONS: Record<string, string> = {
  // Health endpoint — keys as returned by the model
  'Carbon (Climate Change)':
    'Health impacts from greenhouse gas emissions — heat-related mortality, vector-borne disease, malnutrition.',
  'Climate Change':
    'Health impacts from greenhouse gas emissions — heat-related mortality, vector-borne disease, malnutrition.',
  'Carbon':
    'Health impacts from greenhouse gas emissions — heat-related mortality, vector-borne disease, malnutrition.',
  'Particulate Matter':
    'Fine particles (PM2.5) from combustion cause respiratory and cardiovascular disease.',
  'Particulate matter formation':
    'Fine particles (PM2.5) from combustion cause respiratory and cardiovascular disease.',
  'Human Toxicity':
    'Cancer and non-cancer health effects from heavy metals, organic pollutants, and carcinogens released during fossil fuel combustion and mining.',
  'Human Toxicity (Cancerous)':
    'Cancer risk from heavy metals and organic pollutants released during fossil fuel combustion and mining.',
  'Human Toxicity (Non-Cancerous)':
    'Non-cancer health effects (neurological, reproductive) from toxic substance exposure.',
  'Human carcinogenic toxicity':
    'Cancer risk from heavy metals and organic pollutants released during fossil fuel combustion and mining.',
  'Human non-carcinogenic toxicity':
    'Non-cancer health effects (neurological, reproductive) from toxic substance exposure.',
  'Ozone Formation':
    'Ground-level ozone from NOx and VOC emissions causes respiratory illness and crop damage.',
  'Ozone formation, Human health':
    'Ground-level ozone from NOx and VOC emissions causes respiratory illness.',
  'Ionizing Radiation':
    'Radiation exposure from nuclear fuel cycle and fossil fuel combustion byproducts.',
  'Ionizing radiation':
    'Radiation exposure from nuclear fuel cycle and fossil fuel combustion byproducts.',
  'Photochemical Oxidants':
    'Ground-level ozone and photochemical smog from NOx and VOC emissions.',
  'Water Depletion':
    'Health and ecosystem effects of water scarcity driven by industrial water use.',
  'Water Use':
    'Health and ecosystem effects of water scarcity driven by industrial water use.',

  // Ecosystem endpoint
  'Terrestrial Acidification':
    'Soil acidification from SO2 and NOx deposits, harming plant communities.',
  'Freshwater Eutrophication':
    'Algal blooms from phosphorus runoff, depleting oxygen and killing aquatic life.',
  'Marine Eutrophication':
    'Nitrogen enrichment of coastal waters, creating dead zones.',
  'Terrestrial Ecotoxicity':
    'Toxic substances accumulating in soil, harming terrestrial organisms.',
  'Freshwater Ecotoxicity':
    'Toxic substances in waterways harming fish and aquatic invertebrates.',
  'Marine Ecotoxicity':
    'Toxic substances harming marine organisms and ecosystems.',
  'Land Use':
    'Habitat destruction and fragmentation from land transformation for energy infrastructure.',
  'Land Use (Fuel Extraction)':
    'Habitat destruction and fragmentation from land transformation for energy infrastructure.',

  // Economic endpoint
  'Mineral Resource Scarcity':
    'Depletion of mineral reserves used in manufacturing and construction.',
  'Mineral resource scarcity':
    'Depletion of mineral reserves used in manufacturing and construction.',
  'Fossil Resource Scarcity':
    'Depletion of fossil fuel reserves — oil, gas, and coal extraction costs.',
  'Fossil resource scarcity':
    'Depletion of fossil fuel reserves — oil, gas, and coal extraction costs.',
  'Fossil Fuel Depletion':
    'Depletion of fossil fuel reserves — oil, gas, and coal extraction costs.',
};

// ---------------------------------------------------------------------------
// Fuel type display names and descriptions
// ---------------------------------------------------------------------------

export const FUEL_DEFINITIONS: Record<string, { label: string; description: string }> = {
  COL: { label: 'Coal', description: 'Coal-fired generation including bituminous, sub-bituminous, and lignite.' },
  NG:  { label: 'Natural Gas', description: 'Natural gas combined-cycle and peaking plants.' },
  NUC: { label: 'Nuclear', description: 'Nuclear fission power plants.' },
  SUN: { label: 'Solar', description: 'Solar photovoltaic and concentrated solar generation.' },
  WND: { label: 'Wind', description: 'Onshore and offshore wind turbine generation.' },
  WAT: { label: 'Hydro', description: 'Conventional and pumped-storage hydroelectric.' },
  OIL: { label: 'Oil', description: 'Petroleum-fired generation.' },
  OTH: { label: 'Other', description: 'Biomass, geothermal, and other generation sources.' },
  GEO: { label: 'Geothermal', description: 'Geothermal steam and binary cycle plants.' },
  BIO: { label: 'Biomass', description: 'Wood, landfill gas, and other biogenic fuels.' },
  WAS: { label: 'Waste', description: 'Municipal solid waste and waste heat generation.' },
  SUN_hist: { label: 'Solar (Historical)', description: 'Historical solar generation in the BA.' },
  WND_hist: { label: 'Wind (Historical)', description: 'Historical wind generation in the BA.' },
};

/**
 * Get a human-readable fuel label, falling back to the raw key with
 * underscores replaced by spaces.
 */
export function getFuelLabel(key: string): string {
  return FUEL_DEFINITIONS[key]?.label ?? key.replace(/_/g, ' ');
}

// ---------------------------------------------------------------------------
// Cross-referencing fuel keys across data sources
//
// The per-fuel generation table keys the model returns are EIA-style codes
// (COL, NG, OIL, SUN, WND, …) or full names ("Natural Gas"). The
// interconnection_breakdown neighbor `displaced_generation` maps use a
// different vocabulary — lowercase US codes ("ng", "coal", "oil") and EU forms
// ("fossil_gas", "hard_coal", "brown_coal", "lignite"). To line the two up we
// fold every key onto a small set of canonical fuel buckets.
// ---------------------------------------------------------------------------

/** Fold any fuel key (EIA code, full name, or interconnection code) onto a
 *  canonical bucket so generation-table rows and interconnection displacement
 *  can be matched. Order matters: compound tokens (coal/gas) are checked first. */
export function canonicalFuelKey(raw: string): string {
  const k = String(raw).toLowerCase().replace(/[^a-z]/g, '');
  if (k === 'ng' || k.includes('naturalgas') || k.includes('fossilgas') || k === 'gas') return 'gas';
  if (k === 'col' || k.includes('coal') || k.includes('lignite')) return 'coal';
  if (k === 'oil' || k.includes('oil') || k.includes('petroleum')) return 'oil';
  if (k === 'nuc' || k.includes('nuclear')) return 'nuclear';
  if (k === 'wat' || k.includes('hydro') || k === 'water') return 'hydro';
  if (k === 'sun' || k.includes('solar')) return 'solar';
  if (k === 'wnd' || k.includes('wind')) return 'wind';
  if (k === 'geo' || k.includes('geothermal')) return 'geothermal';
  if (k === 'bio' || k.includes('biomass') || k.includes('biogas')) return 'biomass';
  if (k === 'was' || k.includes('waste')) return 'waste';
  if (k === 'oth' || k.includes('other')) return 'other';
  return k;
}

/** Minimal shape of interconnection_breakdown needed to sum displacement. */
interface InterconnectionBreakdownLike {
  neighbors?: Array<{ displaced_generation?: Record<string, number> | null } | null> | null;
}

/**
 * Sum the POSITIVE GWh displaced in neighboring balancing authorities, per
 * canonical fuel bucket, across all neighbors in an interconnection_breakdown.
 * Returns {} when the breakdown is null/absent/empty. Values are positive.
 */
export function interconnectionByCanonicalFuel(
  breakdown: InterconnectionBreakdownLike | null | undefined,
): Record<string, number> {
  const out: Record<string, number> = {};
  const neighbors = breakdown?.neighbors;
  if (!Array.isArray(neighbors)) return out;
  for (const n of neighbors) {
    const dg = n?.displaced_generation;
    if (!dg) continue;
    for (const [fuel, gwh] of Object.entries(dg)) {
      if (typeof gwh !== 'number' || !Number.isFinite(gwh)) continue;
      const bucket = canonicalFuelKey(fuel);
      out[bucket] = (out[bucket] ?? 0) + gwh;
    }
  }
  return out;
}

/**
 * Per-fuel interconnection displacement for a generation-table row, expressed
 * in the SAME sign convention as the "Difference" column (negative = displaced
 * / reduced generation). `byFuel` comes from interconnectionByCanonicalFuel.
 * Returns 0 when this fuel has no interconnection displacement.
 */
export function interconnectionDisplacementForFuel(
  fuelKey: string,
  byFuel: Record<string, number>,
): number {
  const disp = byFuel[canonicalFuelKey(fuelKey)] ?? 0;
  return disp !== 0 ? -disp : 0;
}

// ---------------------------------------------------------------------------
// Project lifecycle labels
// ---------------------------------------------------------------------------

export const LIFECYCLE_LABELS = {
  operating: 'Operating',
  prospective: 'Prospective',
} as const;

/**
 * Returns the display label for a project based on its existing_project_flag.
 */
export function getLifecycleLabel(isExisting: boolean): string {
  return isExisting ? LIFECYCLE_LABELS.operating : LIFECYCLE_LABELS.prospective;
}

// ---------------------------------------------------------------------------
// Technology type labels
// ---------------------------------------------------------------------------

export const TECH_LABELS: Record<string, string> = {
  solar_ground: 'Ground-Mount Solar',
  solar_rooftop: 'Rooftop Solar',
  wind_onshore: 'Onshore Wind',
  wind_offshore: 'Offshore Wind',
};
